import { defineStore } from "pinia";
import { getDefaultInfo } from "@/api/getInfo";
import {
  createPersistenceConfig,
  readCache,
  writeCache,
  clearCache,
  isCacheAvailable,
  isCacheExpiring,
  getCacheInfo,
  type CacheInfo,
} from "@/utils/cache";

// ========== 持久化配置 ==========
const PERSISTENCE_CONFIG = createPersistenceConfig("defaultInfo");

interface DefaultInfoType {
  id: number;
  type: string;
  value: string;
}

interface DefaultInfoState {
  defaultInfo: DefaultInfoType[];
}
export type DefaultInfoMap = {
  home_title?: string[];
  home_subtitle?: string[];
  home_aboutus?: string[];
  home_header1?: string[];
  home_header1_subtitle?: string[];
  home_header2?: string[];
  home_header2_subtitle?: string[];
  home_header3?: string[];
  home_header3_subtitle?: string[];
  home_header4?: string[];
  home_header4_subtitle?: string[];
  home_header5?: string[];
  home_header5_subtitle?: string[];
  about_title?: string[];
  about_subtitle?: string[];
  about_history?: string[];
  about_header1?: string[];
  about_header1_subtitle?: string[];
  about_header2?: string[];
  about_header2_subtitle?: string[];
  about_header3?: string[];
  about_header3_subtitle?: string[];
  about_contact?: string[];
  about_contact_info?: string[];
  doctor_title?: string[];
  doctor_subtitle?: string[];
  department_title?: string[];
  department_subtitle?: string[];
  department_imp_title?: string[];
  department_imp_subtitle?: string[];
  news_title?: string[];
  news_subtitle?: string[];
  foot_addr?: string[];
  foot_phone?: string[];
};

export const useDefaultInfoStore = defineStore("defaultInfo", {
  state: (): DefaultInfoState => ({
    defaultInfo: [],
  }),
  getters: {
    getFormateInfo: (state) => {
      const map = state.defaultInfo.reduce<Record<string, any[]>>(
        (acc, item) => {
          const { type, value } = item;
          if (!acc[type]) acc[type] = [];
          acc[type].push(value);
          return acc;
        },
        {},
      );

      // 返回一个 Proxy，确保访问不存在的 key 时得到空数组，避免模板直接读取导致的运行时错误
      return new Proxy(map, {
        get(target, prop) {
          if (typeof prop === "string") {
            return prop in target ? target[prop] : [];
          }
          return (target as any)[prop];
        },
      });
    },
  },
  actions: {
    async fetchDefaultInfo(
      forceRefresh = false,
      silentRefresh = false,
    ): Promise<void> {
      // 1. 检查内存缓存
      if (this.defaultInfo.length > 0 && !forceRefresh) {
        return;
      }
      // 2. 检查 localStorage 缓存
      if (!forceRefresh) {
        const cachedData = readCache<DefaultInfoType[]>(PERSISTENCE_CONFIG);

        if (cachedData && Array.isArray(cachedData)) {
          this.defaultInfo = cachedData;

          // 静默刷新：缓存即将过期时后台异步刷新
          if (silentRefresh && isCacheExpiring(PERSISTENCE_CONFIG)) {
            this.refreshInBackground();
          }
          return;
        }
      }

      // 3. 请求接口
      try {
        const defaultInfo = await getDefaultInfo();
        const data = defaultInfo.data;

        if (!Array.isArray(data)) {
          throw new Error("接口返回数据格式异常");
        }

        this.defaultInfo = data;
        writeCache(PERSISTENCE_CONFIG, data);
      } catch (error) {
        // 降级策略
        if (isCacheAvailable(PERSISTENCE_CONFIG)) {
          const fallbackData = readCache<DefaultInfoType[]>(PERSISTENCE_CONFIG);
          if (fallbackData && Array.isArray(fallbackData)) {
            this.defaultInfo = fallbackData;
            return;
          }
        }

        if (this.defaultInfo.length === 0) {
          throw error;
        }
      }
    },
    async refreshInBackground(): Promise<void> {
      try {
        const defaultInfo = await getDefaultInfo();
        const data = defaultInfo.data;

        if (Array.isArray(data)) {
          this.defaultInfo = data;
          writeCache(PERSISTENCE_CONFIG, data);
        }
      } catch (error) {}
    },

    clearCache(): void {
      this.defaultInfo = [];
      clearCache(PERSISTENCE_CONFIG);
    },

    getCacheInfo(): CacheInfo {
      return getCacheInfo(PERSISTENCE_CONFIG);
    },
  },
});
